import { hasPermission } from '@/lib/permissions'
import type { MembershipScopes, Variable } from '@/types/api'

/**
 * Location-scoped administration (VARIABLES.md §5). A member with scopes is *restricted*:
 * pickers only offer their locations / groups and the displays inside them. The server
 * enforces the same (anything else is a 404) — this only keeps the UI honest.
 */

export type VariablesView = 'global' | 'location' | 'display_group' | 'display' | 'campaign'

export const VIEW_LABELS: Record<VariablesView, string> = {
  global: 'Global',
  location: 'Location',
  display_group: 'Display group',
  display: 'Display',
  campaign: 'Campaign',
}

export function isRestricted(scopes: MembershipScopes | null | undefined): boolean {
  return !!scopes?.restricted
}

export function allowedLocations<T extends { id: string }>(locations: readonly T[], scopes: MembershipScopes | null | undefined): T[] {
  if (!isRestricted(scopes)) return [...locations]
  const set = new Set(scopes!.locations)
  return locations.filter((l) => set.has(l.id))
}

export function allowedGroups<T extends { id: string }>(groups: readonly T[], scopes: MembershipScopes | null | undefined): T[] {
  if (!isRestricted(scopes)) return [...groups]
  const set = new Set(scopes!.display_groups)
  return groups.filter((g) => set.has(g.id))
}

/** Displays inside one of the member's locations or groups. */
export function allowedDisplays<T extends { id: string; location: { id: string } | null; groups: { id: string }[] }>(displays: readonly T[], scopes: MembershipScopes | null | undefined): T[] {
  if (!isRestricted(scopes)) return [...displays]
  const locs = new Set(scopes!.locations)
  const groups = new Set(scopes!.display_groups)
  return displays.filter((d) => (d.location && locs.has(d.location.id)) || d.groups.some((g) => groups.has(g.id)))
}

/** Views offered to the current member. */
export function availableViews(permissions: readonly string[] | null | undefined, scopes: MembershipScopes | null | undefined): VariablesView[] {
  if (!hasPermission(permissions, 'variables.view')) return []
  const restricted = isRestricted(scopes)
  const views: VariablesView[] = ['global']
  if (hasPermission(permissions, 'displays.view')) {
    if (!restricted || scopes!.locations.length > 0) views.push('location')
    views.push('display_group', 'display')
    if (restricted && scopes!.display_groups.length === 0) views.splice(views.indexOf('display_group'), 1)
  }
  // Campaign values span locations: never for restricted members; listing campaigns needs campaigns.manage.
  if (!restricted && hasPermission(permissions, 'campaigns.manage')) views.push('campaign')
  return views
}

/** The view a member lands on: global for administrators, their first scope otherwise. */
export function defaultView(views: readonly VariablesView[], permissions: readonly string[] | null | undefined): VariablesView {
  if (hasPermission(permissions, 'variables.manage') || views.length === 0) return 'global'
  return views.find((v) => v !== 'global') ?? 'global'
}

/** Whether the member may change the tenant ("global") value of a definition. */
export function canEditGlobalValue(variable: Pick<Variable, 'editable_by'>, permissions: readonly string[] | null | undefined): boolean {
  if (!hasPermission(permissions, 'variables.manage')) return false
  return !variable.editable_by || (permissions ?? []).includes(variable.editable_by)
}

/** Whether a definition can carry values at a scope at all (`overridable_at`). */
export function overridableAt(variable: Pick<Variable, 'overridable_at'>, view: VariablesView): boolean {
  return view === 'global' || variable.overridable_at.includes(view)
}
